import React, { useRef, useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { Search, Users, FileText, Calendar, MoreHorizontal, Mail, Phone, Download, Loader2, AlertCircle, RefreshCw, Check, X, MessageCircle, Eye, MapPin, Calendar as CalendarIcon, Building, User, GraduationCap, Briefcase, Clock, ExternalLink, ChevronDown, ChevronUp, Pause } from 'lucide-react';
import axios from 'axios';
import { getToken } from "../../utils/tokenUtils";
import ReactPaginate from 'react-paginate';
import Modal from "../../components/UI/Modal";
import CampaignForm from "../../components/Form/CampaignForm";
import Notification from "../../components/UI/Notification";
import { useCampaigns } from "../../contexts/CampaignsContext";
import { AuthContext } from "../../contexts/AuthContext";
import { getRoleBasedRoute } from "../../utils/routeUtils";

const RecruitmentResponses = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedForm, setSelectedForm] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [comments, setComments] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({})
  const [approvedCount, setApprovedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const { addCampaign } = useCampaigns();
  const [notification, setNotification] = useState(null);
  
  // New state for detail view
  const [showDetailView, setShowDetailView] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [selectedRequestForCampaign, setSelectedRequestForCampaign] = useState(null);
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [expandedFormId, setExpandedFormId] = useState(null);
  const [submissionsSortOrder, setSubmissionsSortOrder] = useState({}); // { [formId]: 'newest' | 'oldest' }
  const [submissionsStatusFilter, setSubmissionsStatusFilter] = useState({}); // { [formId]: 'all' | 'pending' | 'approved' | 'rejected' }
  const [submissionsSearch, setSubmissionsSearch] = useState({}); // { [formId]: string }

  // Add these lines for scroll-to-submission feature
  const submissionRefs = useRef({});
  const [scrollToSubmissionId, setScrollToSubmissionId] = useState(null);
  
  // Move filteredForms definition here, before useEffect
  const filteredForms = data?.data?.filter(form =>
    form.form_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    form.submissions.some(sub => 
      (sub.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (sub.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (sub.tyuhj?.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  ) || [];

  // Add the missing handlePageChange function
  const handlePageChange = (selectedPage) => {
    fetchData(selectedPage);
  };

  const token = getToken();
  const { roles } = useContext(AuthContext);
  const isHR = roles.includes("HR");

  const countSubmissionStatuses = (formsData) => {
    let approved = 0;
    let rejected = 0;

    formsData.forEach(form => {
      form.submissions?.forEach(submission => {
        if (submission.status === 'approved') approved++;
        else if (submission.status === 'rejected') rejected++;
      });
    });

    return { approved, rejected };
  };

  const handleCampaignSubmit = async (campaignData) => {
    try {
      const response = await axios.post(
        "https://hrmsnew.prepstripe.com/api/form/submission/convert-to-campaign",
        campaignData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response?.status === 200 || response?.status === 201) {
        setNotification({
          message: "Hiring request created successfully.",
          type: "success"
        });
        setIsCampaignModalOpen(false);
        setSelectedRequestForCampaign(null);
        // Redirect to the newly created campaign details
        const campaignId = response?.data?.data?.id || response?.data?.id;
        if (campaignId) {
          setTimeout(() => {
            navigate(`${getRoleBasedRoute("/admin/campaigns", roles)}?campaign_id=${campaignId}`);
          }, 2000);
        }
      } else {
        setNotification({
          message: `${response?.data?.message || "Unknown error occurred while creating the campaign."}`,
          type: "error"
        });
      }
      
    } catch (error) {
      setNotification({
        message: error?.response?.data?.message || "Failed to create campaign.",
        type: "error"
      });
      console.error("Campaign creation error:", error);
    }
  };

  // update status of submission
  const handleStatusUpdate = async (formId, submissionId, status) => {
    setActionLoading(true);
    try {
      await axios.patch(`https://hrmsnew.prepstripe.com/api/${formId}/form-status/${submissionId}`, {
        status: status,
        comments: comments || (status === 'approved' ? 'approved' : 'rejected')
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Refresh data after successful update
      await fetchData();
      setShowModal(false);
      setComments('');
      setModalData(null);
      setScrollToSubmissionId(submissionId); // Set the target to scroll to
    } catch (err) {
      console.error('Status update error:', err);
      setError(err.response?.data?.message || 'Failed to update status');
    }
    setActionLoading(false);
  };

  const openModal = (formId, submission, action) => {
    setModalData({ formId, submission, action });
    setComments('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalData(null);
    setComments('');
  };

   const closeCampaignModal = () => {
    setIsCampaignModalOpen(false);
    setSelectedRequestForCampaign(null);
  };

  const openCampaignModal = (formId, submission) => {
    const today = new Date();
    const formattedToday = today.toISOString().split("T")[0];
    const campaignData = {
      ...submission,
      form_id: formId,
      submission_id: submission.submission_id,
      hiring_request_id: formId,
      to_date: submission.deadline,
      from_date: formattedToday,
      starting_salary_from: "",
      starting_salary_to: "",
      ctc: submission.stipend_ctc_per_month_in_rs,
      no_of_positions: submission.noof_positions || submission.noof_positions || '',
    };
    setSelectedRequestForCampaign(campaignData);
    setIsCampaignModalOpen(true);
  };

  // New functions for detail view
  const openDetailView = (formId, submission, formName) => {
    setDetailData({ formId, submission, formName });
    setShowDetailView(true);
  };

  const closeDetailView = () => {
    setShowDetailView(false);
    setDetailData(null);
  };

  // Fetch data from API
  const fetchData = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`https://hrmsnew.prepstripe.com/api/recruitment-form-responses?page=${page}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.success) {
        setData(response.data);
        setCurrentPage(response?.data?.meta?.current_page);
        setPagination({
          totalPages: response?.data?.meta?.last_page,
          currentPage: response?.data?.meta?.current_page,
          perPage: response?.data?.meta?.per_page,
        }); 
        const { approved, rejected } = countSubmissionStatuses(response.data.data);
        setApprovedCount(approved);
        setRejectedCount(rejected);
      } else {
        setError('Invalid response format from API');
      }
    } catch (err) {
      console.error('API Error:', err);
      setError(err.response?.data?.message || 'Failed to fetch data. Please check your API configuration and bearer token.');
    }
    setLoading(false);
  };

  useEffect(() => {
    // Fetch metrics
    const fetchMetrics = async () => {
      setMetricsLoading(true);
      try {
        const response = await axios.get('https://hrmsnew.prepstripe.com/api/recruitment-form-metrics', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        setMetrics(response.data.data);
      } catch (err) {
        setMetrics(null);
      } finally {
        setMetricsLoading(false);
      }
    };
    fetchMetrics();
    fetchData(currentPage);
  }, []);

  // --- SCROLL TO SUBMISSION LOGIC ---
  useEffect(() => {
    if (scrollToSubmissionId && submissionRefs.current[scrollToSubmissionId]) {
      submissionRefs.current[scrollToSubmissionId].scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      setScrollToSubmissionId(null);
    }
  }, [filteredForms, scrollToSubmissionId]);
  // --- END SCROLL LOGIC ---

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalSubmissions = () => {
    if (!data?.data) return 0;
    return data.data.reduce((total, form) => total + form.submissions.length, 0);
  };

  // Helper function to render submission field
  const renderSubmissionField = (key, value, icon = null) => {
    if (!value || value === '' || value === null || value === undefined) return null;
    
    const getFieldLabel = (key) => {
      const labelMap = {
        candidate_name: 'Full Name',
        tyuhj: 'Name',
        email: 'Email Address',
        contact_number: 'Phone Number',
        ytu: 'Message/Notes',
        resume: 'Resume',
        submission_id: 'Submission ID',
        created_at: 'Submitted Date',
        updated_at: 'Last Updated',
        status: 'Status',
      };
      return labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const formatFieldValue = (key, value) => {
      if (key === 'created_at' || key === 'updated_at') {
        return formatDate(value);
      }
      if (key === 'resume' && value) {
        return (
          <div className="flex items-center gap-2">
            <span className="text-blue-600">Resume attached</span>
            <button className="text-blue-600 hover:text-blue-800 underline text-sm">
              Download
            </button>
          </div>
        );
      }
      if (key === 'email' && value) {
        return (
          <a href={`mailto:${value}`} className="text-blue-600 hover:text-blue-800 underline">
            {value}
          </a>
        );
      }
      if (key === 'contact_number' && value) {
        return (
          <a href={`tel:${value}`} className="text-blue-600 hover:text-blue-800 underline">
            {value}
          </a>
        );
      }
      if (key === 'status' && value) {
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            value === 'approved' 
              ? 'bg-green-100 text-green-800' 
              : value === 'rejected'
              ? 'bg-red-100 text-red-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </span>
        );
      }
      return value;
    };

    return (
      <div key={key} className="flex flex-col sm:flex-row sm:items-start gap-2 py-3 border-b border-gray-100 last:border-b-0">
        <div className="flex items-center gap-2 min-w-0 sm:w-1/3">
          {icon}
          <span className="font-medium text-gray-700 text-sm">{getFieldLabel(key)}:</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-gray-900 break-words text-sm">
            {formatFieldValue(key, value)}
          </div>
        </div>
      </div>
    );
  };

  // Helper to format numbers as integer (no decimals)
  const formatInt = (val) => {
    if (val === null || val === undefined) return 0;
    const num = Number(val);
    return isNaN(num) ? val : Math.round(num);
  };
  // Helper to format percent (e.g. '+25.00%' => '+25%')
  const formatPercent = (val) => {
    if (typeof val !== 'string') return val;
    const match = val.match(/^([+-]?)(\d+)(?:\.(\d+))?%$/);
    if (!match) return val;
    const sign = match[1];
    const num = Math.round(Number(match[2] + (match[3] ? '.' + match[3] : '')));
    return `${sign}${num}%`;
  };

  // Helper to get color class for percent change
  const percentColor = (val) => {
    if (typeof val !== 'string') return '';
    if (val.trim().startsWith('+')) return 'text-green-600';
    if (val.trim().startsWith('-')) return 'text-red-600';
    return '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-lg text-gray-600">Loading recruitment data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">Error Loading Data</h3>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <button
            onClick={pagination.currentPage}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}
      
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-black">
                Requisition Form Responses
              </h1>
              <p className="text-gray-600 mt-1">Manage and review requisition form responses</p>
            </div>
            <button
              onClick={() => fetchData(pagination.currentPage)}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-xl transition-colors flex items-center gap-2 w-fit"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Forms Card */}
          <div className="group bg-white/95 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-xl border border-white/60 p-6 transition-all duration-300 hover:scale-105 hover:bg-white/100">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Total Forms</p>
                <p className="text-3xl font-bold text-indigo-600 mb-1">
                  {metricsLoading ? '...' : formatInt(metrics?.total_active_forms)}
                </p>
                <div className="flex items-center">
                  <div className="w-8 h-1 bg-indigo-200 rounded-full overflow-hidden">
                    <div className="w-full h-full bg-indigo-500 rounded-full"></div>
                  </div>
                  <span className="text-xs text-gray-400 ml-2">Active</span>
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-indigo-600 rounded-2xl transform transition-transform duration-300"></div>
                <div className="relative bg-indigo-500/10 rounded-2xl p-3 group-hover:bg-indigo-500/20 transition-colors duration-300">
                  <FileText className="h-8 w-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Total Submissions Card */}
          <div className="group bg-white/95 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-xl border border-white/60 p-6 transition-all duration-300 hover:scale-105 hover:bg-white/100">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Total Submissions</p>
                <p className="text-3xl font-bold text-blue-600 mb-1">
                  {metricsLoading ? '...' : formatInt(metrics?.total_submissions)}
                </p>
                <div className="flex items-center">
                  <div className="w-8 h-1 bg-blue-200 rounded-full overflow-hidden">
                    <div className="w-3/4 h-full bg-blue-500 rounded-full"></div>
                  </div>
                  <span className="text-xs text-gray-400 ml-2">{metricsLoading ? '...' : formatPercent(metrics?.response_rate)} response rate</span>
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-blue-600 rounded-2xl transform transition-transform duration-300"></div>
                <div className="relative bg-blue-500/10 rounded-2xl p-3 group-hover:bg-blue-500/20 transition-colors duration-300">
                  <Users className="h-8 w-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Approved Submissions Card */}
          <div className="group bg-white/95 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-xl border border-white/60 p-6 transition-all duration-300 hover:scale-105 hover:bg-white/100">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Approved</p>
                <p className="text-3xl font-bold text-emerald-600 mb-1">
                  {metricsLoading ? '...' : formatInt(metrics?.approved_count)}
                </p>
                <div className="flex items-center">
                  <div className="w-8 h-1 bg-emerald-200 rounded-full overflow-hidden">
                    <div className="w-5/6 h-full bg-emerald-500 rounded-full"></div>
                  </div>
                  <span className="text-xs text-emerald-600 ml-2 font-medium">
                     {metricsLoading ? '...' : (
                       <span className={percentColor(metrics?.percent_change?.approved)}>
                         {formatPercent(metrics?.percent_change?.approved)}
                       </span>
                     )} this week
                  </span>
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-emerald-600 rounded-2xl transform transition-transform duration-300"></div>
                <div className="relative bg-emerald-500/10 rounded-2xl p-3 group-hover:bg-emerald-500/20 transition-colors duration-300">
                  <Check className="h-8 w-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Rejected Submissions Card */}
          <div className="group bg-white/95 backdrop-blur-sm rounded-3xl shadow-lg hover:shadow-xl border border-white/60 p-6 transition-all duration-300 hover:scale-105 hover:bg-white/100">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1">Rejected</p>
                <p className="text-3xl font-bold text-red-600 mb-1">
                  {metricsLoading ? '...' : formatInt(metrics?.rejected_count)}
                </p>
                <div className="flex items-center">
                  <div className="w-8 h-1 bg-red-200 rounded-full overflow-hidden">
                    <div className="w-1/4 h-full bg-red-500 rounded-full"></div>
                  </div>
                  <span className="text-xs text-red-600 ml-2 font-medium">
                     {metricsLoading ? '...' : (
                       <span className={percentColor(metrics?.percent_change?.rejected)}>
                         {formatPercent(metrics?.percent_change?.rejected)}
                       </span>
                     )} this week
                  </span>
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-red-600 rounded-2xl transform transition-transform duration-300"></div>
                <div className="relative bg-red-500/10 rounded-2xl p-3 group-hover:bg-red-500/20 transition-colors duration-300">
                  <X className="h-8 w-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Forms Table */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden">
          {/* Table Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h3 className="text-lg font-semibold text-gray-900">All Submissions</h3>
              <div className="flex items-center gap-4">
                <input
                  type="text"
                  className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search submissions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ minWidth: 200 }}
                />
                <select
                  className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={submissionsStatusFilter['all'] || 'all'}
                  onChange={e => setSubmissionsStatusFilter({ all: e.target.value })}
                >
                  <option value="all">All Status</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="hold">Hold</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-auto">
            <table className="w-full overflow-auto">
              <thead className="bg-gray-50">
                <tr>
                  {(() => {
                    // Get the first submission to determine columns
                    const firstSubmission = filteredForms.flatMap(form => form.submissions)[0];
                    if (!firstSubmission) return null;
                    
                    // Get all keys excluding the specified fields
                    const allKeys = Object.keys(firstSubmission).filter(
                      (key) => !['id', 'submission_id', 'created_at', 'updated_at', 'created_by', 'form_id'].includes(key)
                    );
                    
                    // Take only the first 4 columns
                    const displayKeys = allKeys.slice(0, 4);
                    
                    return displayKeys.map((key) => (
                      <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-0">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </th>
                    ));
                  })()}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredForms.flatMap(form => 
                  form.submissions
                    .filter(sub => {
                      const search = searchTerm.toLowerCase();
                      if (!search) return true;
                      return (
                        (sub.candidate_name?.toLowerCase().includes(search)) ||
                        (sub.tyuhj?.toLowerCase().includes(search)) ||
                        (sub.email?.toLowerCase().includes(search)) ||
                        (sub.contact_number?.toLowerCase().includes(search)) ||
                        form.form_name.toLowerCase().includes(search)
                      );
                    })
                    .filter(sub => {
                      const status = submissionsStatusFilter['all'] || 'all';
                      if (status === 'all') return true;
                      if (status === 'pending') return !sub.status || sub.status === 'pending';
                      return sub.status === status;
                    })
                    .map((submission) => {
                      // Get the first submission to determine columns
                      const firstSubmission = filteredForms.flatMap(form => form.submissions)[0];
                      if (!firstSubmission) return null;
                      
                      // Get all keys excluding the specified fields
                      const allKeys = Object.keys(firstSubmission).filter(
                        (key) => !['id', 'submission_id', 'created_at', 'updated_at', 'created_by', 'form_id'].includes(key)
                      );
                      
                      // Take only the first 4 columns
                      const displayKeys = allKeys.slice(0, 4);
                      
                      return (
                        <tr 
                          key={`${form.id}-${submission.id}`}
                          ref={el => submissionRefs.current[submission.id] = el}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          {displayKeys.map((key) => (
                            <td key={key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 min-w-0">
                              {(() => {
                                const value = submission[key];
                                
                                // Handle email links
                                if (key === 'email' && value) {
                                  return (
                                    <a href={`mailto:${value}`} className="text-blue-600 hover:text-blue-800 underline truncate block max-w-xs" title={value}>
                                      {value}
                                    </a>
                                  );
                                }
                                
                                // Handle phone links
                                if (key === 'contact_number' && value) {
                                  return (
                                    <a href={`tel:${value}`} className="text-blue-600 hover:text-blue-800 underline truncate block max-w-xs" title={value}>
                                      {value}
                                    </a>
                                  );
                                }
                                
                                // Handle status with color coding
                                if (key === 'status' && value) {
                                  return (
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                      value === 'approved' 
                                        ? 'bg-green-100 text-green-800' 
                                        : value === 'rejected'
                                        ? 'bg-red-100 text-red-800'
                                        : value === 'hold'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {value.charAt(0).toUpperCase() + value.slice(1)}
                                    </span>
                                  );
                                }
                                
                                // Handle resume/file links
                                if (key === 'resume' && value) {
                                  const isFilePath = typeof value === 'string' && 
                                    value.includes('form_uploads/') && 
                                    /\.(pdf|doc|docx|jpg|jpeg|png|gif|txt|csv|xlsx|xls)$/i.test(value);
                                  
                                  if (isFilePath) {
                                    const fileName = value.split('/').pop() || value;
                                    const cleanFileName = fileName.replace(/_\d{8}_\d{6}_[a-zA-Z0-9]+(\.[a-zA-Z]+)$/, '$1');
                                    
                                    return (
                                      <a
                                        href={`https://hrmsnew.prepstripe.com/storage/${value}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 underline text-sm flex items-center gap-1 truncate max-w-xs"
                                        title={cleanFileName}
                                      >
                                        View File
                                        <FileText className="h-3 w-3 flex-shrink-0" />
                                      </a>
                                    );
                                  }
                                }
                                
                                // Default rendering
                                return (
                                  <div className="truncate max-w-xs" title={value}>
                                    {value || 'N/A'}
                                  </div>
                                );
                              })()}
                            </td>
                          ))}
                          
                          {/* Actions Column */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openDetailView(form.id, submission, form.form_name)}
                                className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md transition-colors flex items-center gap-1"
                                title="View Details">
                                <Eye className="h-4 w-4" />
                                View Details
                              </button>

                              {isHR && submission.status === "approved" && submission.is_campaign === 0 && (
                                <button
                                  onClick={() => openCampaignModal(form.id, submission)}
                                  className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-md transition-colors flex items-center gap-1"
                                  title="Convert to Campaign">
                                  <ExternalLink className="h-4 w-4" />
                                  Convert to Campaign
                                </button>
                              )}

                              {submission.is_campaign === 1 && (
                                <Link
                                  to={`${window.location.origin}/campaign/${submission.campaign_id}`} 
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-purple-600 hover:text-purple-900 bg-purple-50 hover:bg-purple-100 px-3 py-1 rounded-md transition-colors flex items-center gap-1"
                                  title="View Campaign">
                                  <ExternalLink className="h-4 w-4" />
                                  View Campaign
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {filteredForms.flatMap(form => form.submissions).length === 0 && (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No submissions found</h3>
              <p className="text-gray-500">Try adjusting your search criteria</p>
            </div>
          )}
        </div>
        
        {/* Pagination */}
        <ReactPaginate
          breakLabel="..."
          nextLabel="Next >"
          onPageChange={(event) => handlePageChange(event.selected + 1)}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          pageCount={pagination.totalPages}
          previousLabel="< Prev"
          previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
          nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
          renderOnZeroPageCount={null}
          containerClassName="flex items-center justify-center mt-4 font-poppins"
          pageClassName="mx-1 px-3 py-1 border rounded dark:text-white font-poppins"
          activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
        />

        {/* The original filteredForms.length === 0 block is removed as the table now shows all submissions */}
      </div>

      {/* Full Screen Detail View Modal */}
      {showDetailView && detailData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full h-full overflow-hidden">
            {/* Header */}
            <div className="bg-gray-100 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-black">Submission Details</h3>
                <p className="text-gray-600 text-sm mt-1">
                  {detailData.formName} • #{detailData.submission.submission_id}
                </p>
              </div>
              <button
                onClick={closeDetailView}
                className="text-black hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6 h-[calc(90vh-100px)]">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">
                {/* Left Column - Personal Information & Documents - Scrollable */}
                <div className="space-y-6 overflow-y-auto pr-2">
                  <div>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-1">
                      {renderSubmissionField('candidate_name', detailData.submission.candidate_name, <User className="h-4 w-4 text-gray-500" />)}
                      {renderSubmissionField('tyuhj', detailData.submission.tyuhj, <User className="h-4 w-4 text-gray-500" />)}
                      {renderSubmissionField('email', detailData.submission.email, <Mail className="h-4 w-4 text-gray-500" />)}
                      {renderSubmissionField('contact_number', detailData.submission.contact_number, <Phone className="h-4 w-4 text-gray-500" />)}
                      
                      {/* Additional Information moved here */}
                      {Object.entries(detailData.submission)
                        .filter(([key, value]) => 
                          !['candidate_name', 'tyuhj', 'email', 'contact_number', 'resume', 'ytu', 'submission_id', 'status', 'created_at', 'updated_at', 'id'].includes(key) &&
                          value !== null && value !== undefined && value !== ''
                        )
                        .map(([key, value]) => {
                          const isFilePath = typeof value === 'string' && 
                            value.includes('form_uploads/') && 
                            /\.(pdf|doc|docx|jpg|jpeg|png|gif|txt|csv|xlsx|xls)$/i.test(value);
                          
                          if (isFilePath) {
                            const fileName = value.split('/').pop() || value;
                            const cleanFileName = fileName.replace(/_\d{8}_\d{6}_[a-zA-Z0-9]+(\.[a-zA-Z]+)$/, '$1');
                            
                            return (
                              <div key={key} className="flex items-center gap-2 py-1">
                                <FileText className="h-4 w-4 text-gray-500" />
                                <span className="text-sm text-gray-600 font-medium min-w-0 flex-shrink-0">
                                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                                </span>
                                <a
                                  href={`https://hrmsnew.prepstripe.com/storage/${value}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 underline text-sm truncate flex items-center gap-1"
                                  title={cleanFileName}
                                >
                                  {/* {cleanFileName} */}
                                  View File
                                  <FileText className="h-3 w-3 flex-shrink-0" />
                                </a>
                              </div>
                            );
                          }
                          
                          return renderSubmissionField(key, value);
                        })
                      }
                    </div>
                  </div>

                  {/* Documents */}
                  <div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {(() => {
                        const isFilePath = typeof detailData.submission.resume === 'string' && 
                          detailData.submission.resume.includes('form_uploads/') && 
                          /\.(pdf|doc|docx|jpg|jpeg|png|gif|txt|csv|xlsx|xls)$/i.test(detailData.submission.resume);
                        
                        if (isFilePath) {
                          const fileName = detailData.submission.resume.split('/').pop() || detailData.submission.resume;
                          const cleanFileName = fileName.replace(/_\d{8}_\d{6}_[a-zA-Z0-9]+(\.[a-zA-Z]+)$/, '$1');
                          
                          return (
                            <div className="flex items-center gap-2 py-1">
                              <FileText className="h-4 w-4 text-gray-500" />
                              <span className="text-sm text-gray-600 font-medium min-w-0 flex-shrink-0">Resume:</span>
                              <a
                                href={`/${detailData.submission.resume}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline text-sm truncate flex items-center gap-1"
                                title={cleanFileName}
                              >
                                {cleanFileName}
                                <FileText className="h-3 w-3 flex-shrink-0" />
                              </a>
                            </div>
                          );
                        }
                        
                        return renderSubmissionField('resume', detailData.submission.resume, <FileText className="h-4 w-4 text-gray-500" />);
                      })()}
                      {/* {!detailData.submission.resume && (
                        <p className="text-gray-500 text-sm">No documents uploaded</p>
                      )} */}
                    </div>
                  </div>

                  {/* Additional Message */}
                  {detailData.submission.ytu && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <MessageCircle className="h-5 w-5 text-blue-600" />
                        Message/Notes
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-gray-900 text-sm leading-relaxed">
                          {detailData.submission.ytu}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Column - System Information & Actions - Fixed Height */}
                <div className="space-y-6 flex flex-col h-full">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-blue-600" />
                      Submission Details
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-1">
                      {renderSubmissionField('submission_id', detailData.submission.submission_id, <FileText className="h-4 w-4 text-gray-500" />)}
                      {renderSubmissionField('status', detailData.submission.status, <Check className="h-4 w-4 text-gray-500" />)}
                      {renderSubmissionField('created_at', detailData.submission.created_at, <CalendarIcon className="h-4 w-4 text-gray-500" />)}
                      {renderSubmissionField('updated_at', detailData.submission.updated_at, <Clock className="h-4 w-4 text-gray-500" />)}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="bg-gray-50 rounded-lg p-4 flex-shrink-0">
                    <div className="flex flex-wrap gap-3">
                      {isHR && (!detailData.submission.status || detailData.submission.status !== 'approved') && (
                        <>
                          <br></br>
                          {/* <h4 className="text-lg font-semibold text-gray-900 mb-4">Actions</h4> */}
                          <button
                            onClick={() => {
                              closeDetailView();
                              openModal(detailData.formId, detailData.submission, 'approved');
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2">
                            <Check className="h-4 w-4" />
                            Approve
                          </button>
                          
                          <button
                            onClick={() => {
                              closeDetailView();
                              openModal(detailData.formId, detailData.submission, 'rejected');
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2">
                            <X className="h-4 w-4" />
                            Reject
                          </button>
                          
                          {detailData.submission.status !== 'hold' && (
                            <button
                              onClick={() => {
                                closeDetailView();
                                openModal(detailData.formId, detailData.submission, 'hold');
                              }}
                              className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2">
                              <Pause className="h-4 w-4" />
                              Hold
                            </button>
                          )}
                        </>
                      )}

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval/Rejection Modal */}
      {showModal && modalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className={`px-6 py-4 rounded-t-2xl ${
              modalData.action === 'approved' ? 'bg-green-500' : modalData.action === 'rejected' ? 'bg-red-500' : 'bg-yellow-400'
            }`}>
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                {modalData.action === 'approved' ? (
                  <>
                    <Check className="h-5 w-5" />
                    Approve Submission
                  </>
                ) : modalData.action === 'rejected' ? (
                  <>
                    <X className="h-5 w-5" />
                    Reject Submission
                  </>
                ) : (
                  <>
                    <Pause className="h-5 w-5" />
                    Hold Submission
                  </>
                )}
              </h3>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-700 mb-2">
                  <strong>Requested By:</strong> {modalData.submission['request_by_-_full_name'] || modalData.submission.request_by || modalData.submission.tyuhj || 'Unknown'}
                </p>
                <p className="text-gray-700 mb-4">
                  <strong>Submission ID:</strong> #{modalData.submission.submission_id}
                </p>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments (Optional)
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder={`Add comments for ${modalData.action === 'approved' ? 'approval' : modalData.action === 'rejected' ? 'rejection' : 'hold'}...`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  rows="3"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={closeModal}
                  disabled={actionLoading}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleStatusUpdate(modalData.formId, modalData.submission.submission_id, modalData.action)}
                  disabled={actionLoading}
                  className={`flex-1 font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 text-white flex items-center justify-center gap-2 ${
                    modalData.action === 'approved' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : modalData.action === 'rejected'
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-yellow-400 hover:bg-yellow-500'
                  }`}
                >
                  {actionLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                     {modalData.action === 'approved' ? (
                       <Check className="h-4 w-4" />
                     ) : modalData.action === 'rejected' ? (
                       <X className="h-4 w-4" />
                     ) : (
                       <Pause className="h-4 w-4" />
                     )}
                     {modalData.action === 'approved' ? 'Approve' : modalData.action === 'rejected' ? 'Reject' : 'Hold'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Campaign Conversion */}
      <Modal
        isOpen={isCampaignModalOpen}
        onRequestClose={closeCampaignModal}
        title="Convert to Campaign"
        fullSize={true}>
        {selectedRequestForCampaign && (
          <CampaignForm
            closeModal={closeCampaignModal}
            onSubmit={handleCampaignSubmit}
            initialValues={selectedRequestForCampaign}
          />
        )}
      </Modal>
    </div>
  );
};

export default RecruitmentResponses;